import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { formatDateTime } from '../utils/dateFormat';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { 
  Shield, 
  Loader2, 
  Search,
  Download,
  RefreshCw,
  User,
  FileText,
  Settings,
  Calendar,
  Filter,
  ExternalLink
} from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ACTION_COLORS = {
  'create': 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
  'update': 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
  'delete': 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
  'login': 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400',
  'view': 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400',
  'default': 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400'
};

const AuditLogsPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [logs, setLogs] = useState([]);
  const [filteredLogs, setFilteredLogs] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [actionFilter, setActionFilter] = useState('_all');
  const [dateFilter, setDateFilter] = useState('_all');
  const [userFilter, setUserFilter] = useState('_all');
  const [exporting, setExporting] = useState(false);
  const [userTimezone, setUserTimezone] = useState('America/Chicago');
  const [stats, setStats] = useState({ total: 0, today: 0, thisWeek: 0 });
  const [allUsers, setAllUsers] = useState([]);

  useEffect(() => {
    fetchLogs();
    fetchUserTimezone();
    fetchAllUsers();
  }, []);

  useEffect(() => {
    filterLogs();
  }, [logs, searchTerm, actionFilter, dateFilter, userFilter]);

  const fetchUserTimezone = async () => {
    try {
      const response = await axios.get(`${API}/system/settings`);
      if (response.data?.timezone) {
        setUserTimezone(response.data.timezone);
      }
    } catch (error) {
      // Use default timezone
    }
  };

  const fetchAllUsers = async () => {
    try {
      const response = await axios.get(`${API}/admin/users`);
      const users = response.data.users || response.data || [];
      // Sort users alphabetically by name
      const sortedUsers = users.sort((a, b) => 
        (a.name || a.email || '').toLowerCase().localeCompare((b.name || b.email || '').toLowerCase())
      );
      setAllUsers(sortedUsers);
    } catch (error) {
      console.error('Failed to fetch users for filter:', error);
      // Fallback: use unique users from logs
    }
  };

  const fetchLogs = async () => {
    try {
      const response = await axios.get(`${API}/reports/audit`);
      const logsData = response.data.logs || [];
      setLogs(logsData);
      
      // Calculate stats
      const now = new Date();
      const today = now.toISOString().split('T')[0];
      const weekAgo = new Date(now.setDate(now.getDate() - 7)).toISOString();
      
      setStats({
        total: logsData.length,
        today: logsData.filter(l => l.timestamp?.startsWith(today)).length,
        thisWeek: logsData.filter(l => l.timestamp >= weekAgo).length
      });
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Access denied. Admin privileges required.');
      } else {
        toast.error('Failed to load audit logs');
      }
    } finally {
      setLoading(false);
    }
  };

  const filterLogs = () => {
    let filtered = [...logs];
    
    // Search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(log => 
        log.action?.toLowerCase().includes(term) ||
        log.user_name?.toLowerCase().includes(term) ||
        log.target_id?.toLowerCase().includes(term) ||
        JSON.stringify(log.details || {}).toLowerCase().includes(term)
      );
    }
    
    // Action filter
    if (actionFilter && actionFilter !== '_all') {
      filtered = filtered.filter(log => log.action?.includes(actionFilter));
    }
    
    // User filter
    if (userFilter && userFilter !== '_all') {
      filtered = filtered.filter(log => log.user_name === userFilter || log.user_id === userFilter);
    }
    
    // Date filter
    if (dateFilter && dateFilter !== '_all') {
      const now = new Date();
      let cutoff;
      
      switch (dateFilter) {
        case 'today':
          cutoff = now.toISOString().split('T')[0];
          filtered = filtered.filter(log => log.timestamp?.startsWith(cutoff));
          break;
        case 'week':
          cutoff = new Date(now.setDate(now.getDate() - 7)).toISOString();
          filtered = filtered.filter(log => log.timestamp >= cutoff);
          break;
        case 'month':
          cutoff = new Date(now.setMonth(now.getMonth() - 1)).toISOString();
          filtered = filtered.filter(log => log.timestamp >= cutoff);
          break;
        default:
          break;
      }
    }
    
    setFilteredLogs(filtered);
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '-';
    try {
      return formatInTimeZone(parseISO(timestamp), userTimezone, 'MMM d, yyyy h:mm:ss a');
    } catch {
      return timestamp;
    }
  };

  const getActionColor = (action) => {
    if (!action) return ACTION_COLORS.default;
    const lowerAction = action.toLowerCase();
    if (lowerAction.includes('create') || lowerAction.includes('add')) return ACTION_COLORS.create;
    if (lowerAction.includes('update') || lowerAction.includes('edit')) return ACTION_COLORS.update;
    if (lowerAction.includes('delete') || lowerAction.includes('remove')) return ACTION_COLORS.delete;
    if (lowerAction.includes('login') || lowerAction.includes('auth')) return ACTION_COLORS.login;
    if (lowerAction.includes('view')) return ACTION_COLORS.view;
    return ACTION_COLORS.default;
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const response = await axios.get(`${API}/reports/export/audit`);
      const { headers, data } = response.data;
      
      const csvContent = [
        headers.join(','),
        ...data.map(row => headers.map(h => `"${(row[h] || '').toString().replace(/"/g, '""')}"`).join(','))
      ].join('\n');
      
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `myhelp_audit_logs_${new Date().toISOString().split('T')[0]}.csv`;
      link.click();
      
      toast.success('Audit logs exported successfully');
    } catch (error) {
      toast.error('Failed to export audit logs');
    } finally {
      setExporting(false);
    }
  };

  const uniqueActions = [...new Set(logs.map(l => l.action?.split('_')[0]).filter(Boolean))];
  
  // Use all users from user management, fallback to unique users from logs if not loaded
  const userFilterOptions = allUsers.length > 0 
    ? allUsers.map(u => ({ id: u.id, name: u.name || u.email }))
    : [...new Set(logs.map(l => l.user_name).filter(Boolean))].sort((a, b) => 
        a.toLowerCase().localeCompare(b.toLowerCase())
      ).map(name => ({ id: name, name }));

  // Helper function to get navigation link based on entity type and ID
  const getEntityNavigationPath = (log) => {
    const { entity_type, entity_id, target_id, details } = log;
    
    // Use entity_id if available, otherwise fall back to target_id
    const id = entity_id || target_id;
    
    if (!id || id === 'bulk_operation' || id === 'system') return null;
    
    switch (entity_type) {
      case 'participant':
        return `/participants/${id}`;
      case 'case':
        return `/cases/${id}`;
      case 'enrollment':
        // Enrollments are viewed on participant page, try to get participant_id from details
        if (details?.participant_id) {
          return `/participants/${details.participant_id}`;
        }
        return null;
      case 'case_note':
        // Case notes are viewed on case page
        if (details?.case_id) {
          return `/cases/${details.case_id}`;
        }
        return null;
      case 'document':
        // Documents are viewed on case page
        if (details?.case_id) {
          return `/cases/${details.case_id}`;
        }
        return null;
      case 'user':
        return `/admin/users`;
      case 'contract':
        return `/admin/contracts`;
      case 'program_type':
        return `/admin/program-types`;
      case 'service_type':
        return `/admin/service-types`;
      case 'checkin':
        // Check-ins don't have a detail page, but we can navigate to participant if available
        if (details?.participant_id) {
          return `/participants/${details.participant_id}`;
        }
        return null;
      case 'communication':
        // Communications are viewed on participant page
        if (details?.participant_id) {
          return `/participants/${details.participant_id}`;
        }
        return null;
      default:
        // For participant-related actions where entity_type might not be set
        if (log.action?.includes('participant') && id?.startsWith('PRT-')) {
          return `/participants/${id}`;
        }
        if (log.action?.includes('case') && id?.startsWith('CASE-')) {
          return `/cases/${id}`;
        }
        return null;
    }
  };

  const handleNavigateToEntity = (log) => {
    const path = getEntityNavigationPath(log);
    if (path) {
      navigate(path);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="audit-logs-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
            <Shield className="w-8 h-8 text-primary" />
            Audit Logs
          </h1>
          <p className="text-muted-foreground mt-1">
            System activity and security events ({userTimezone})
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchLogs}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          <Button onClick={handleExport} disabled={exporting}>
            {exporting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
            Export CSV
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="card-shadow">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                <FileText className="w-6 h-6 text-primary" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Events</p>
                <p className="text-2xl font-bold">{stats.total.toLocaleString()}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="card-shadow">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                <Calendar className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Today</p>
                <p className="text-2xl font-bold">{stats.today.toLocaleString()}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="card-shadow">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                <User className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">This Week</p>
                <p className="text-2xl font-bold">{stats.thisWeek.toLocaleString()}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="card-shadow">
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search logs..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="search-audit-logs"
              />
            </div>
            <Select value={actionFilter} onValueChange={setActionFilter}>
              <SelectTrigger className="w-full sm:w-40">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Action" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="_all">All Actions</SelectItem>
                {uniqueActions.map(action => (
                  <SelectItem key={action} value={action}>{action}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={userFilter} onValueChange={setUserFilter}>
              <SelectTrigger className="w-full sm:w-48" data-testid="user-filter">
                <User className="w-4 h-4 mr-2" />
                <SelectValue placeholder="User" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="_all">All Users</SelectItem>
                {userFilterOptions.map(user => (
                  <SelectItem key={user.id} value={user.name}>{user.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={dateFilter} onValueChange={setDateFilter}>
              <SelectTrigger className="w-full sm:w-40">
                <Calendar className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Date" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="_all">All Time</SelectItem>
                <SelectItem value="today">Today</SelectItem>
                <SelectItem value="week">Last 7 Days</SelectItem>
                <SelectItem value="month">Last 30 Days</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Logs Table */}
      <Card className="card-shadow">
        <CardHeader>
          <CardTitle>Activity Log</CardTitle>
          <CardDescription>
            Showing {filteredLogs.length} of {logs.length} events
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="max-h-[600px] overflow-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Timestamp</TableHead>
                  <TableHead>User</TableHead>
                  <TableHead>Action</TableHead>
                  <TableHead>Target</TableHead>
                  <TableHead>Details</TableHead>
                  <TableHead className="w-[80px]">Go To</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredLogs.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                      No audit logs found
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredLogs.slice(0, 200).map((log, index) => (
                    <TableRow key={log.id || index}>
                      <TableCell className="whitespace-nowrap text-sm">
                        {formatTimestamp(log.timestamp)}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                            <User className="w-4 h-4 text-primary" />
                          </div>
                          <span className="font-medium">{log.user_name || 'System'}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge className={getActionColor(log.action)}>
                          {log.action}
                        </Badge>
                      </TableCell>
                      <TableCell className="max-w-[150px] truncate">
                        {log.target_id || log.entity_id || '-'}
                      </TableCell>
                      <TableCell className="max-w-[200px]">
                        {log.details ? (
                          <span className="text-xs text-muted-foreground truncate block">
                            {typeof log.details === 'object' 
                              ? JSON.stringify(log.details).substring(0, 50) + '...'
                              : String(log.details).substring(0, 50)}
                          </span>
                        ) : (
                          '-'
                        )}
                      </TableCell>
                      <TableCell>
                        {getEntityNavigationPath(log) ? (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleNavigateToEntity(log)}
                            className="h-8 px-2"
                            data-testid={`go-to-${log.id || index}`}
                          >
                            <ExternalLink className="w-4 h-4" />
                          </Button>
                        ) : (
                          <span className="text-muted-foreground text-xs">-</span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
          {filteredLogs.length > 200 && (
            <p className="text-center text-sm text-muted-foreground mt-4">
              Showing first 200 results. Export to CSV for complete data.
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AuditLogsPage;
